import React from 'react';
import { useTranslation } from 'react-i18next';
import parse, { domToReact } from 'html-react-parser';
import ContentImage from '../ContentImage';
import ExternalLink from '../ExternalLink';
import RecordedMedia, { RecordedMediaPlayer } from '../RecordedMedia';
import { externalLinkSegments, LINK_LABEL_ATTRIBUTE, normalizeExternalUrl } from '../../utils/externalLinkText';
import { QUOTE_ATTRIBUTE } from './placement';
import CensoredContent, { isCensoredImage, isUnavailableImage } from '../CensoredContent';

const textContent = node => node.type === 'text' ? node.data : (node.children || []).map(textContent).join('');
const hasImage = node => node.name === 'img' || (node.children || []).some(hasImage);
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const unsafeContainers = new Set(['iframe', 'object', 'embed', 'script', 'style', 'audio', 'video', 'source', 'track',
  'svg', 'math', 'link', 'meta', 'base', 'form', 'input', 'button', 'textarea', 'template']);
const textTags = new Set(['p', 'div', 'span', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'blockquote', 'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'pre', 'code']);
const imageSource = value => typeof value === 'string' && (
  /^https?:\/\//i.test(value) || /^\/(?!\/)/.test(value) || /^data:image\/(?:png|jpe?g|gif|webp|avif);base64,/i.test(value)
) ? value : undefined;
const mediaKey = id => uuid.test(id) ? id.toLowerCase() : id;

const QuoteSlot = ({ children }) => <>{children}</>;
const UnavailableRecording = () => {
  const { t } = useTranslation('media');
  const label = t('failed', { defaultValue: 'Recording unavailable' });
  return <span className="censored-content" data-media-unavailable="true" role="img" aria-label={label}>
    <span aria-hidden="true">{label}</span>
  </span>;
};
const meaningful = node => {
  if (typeof node === 'string') return Boolean(node.trim());
  if (Array.isArray(node)) return node.some(meaningful);
  if (!React.isValidElement(node)) return false;
  return typeof node.type === 'function' || node.type === 'img' || meaningful(node.props.children);
};

export default function ReactionBody({ html, media, reactionId, quote, textStatus, wrapContent = content => content }) {
  // Do not trust cached/legacy DTO bodies when their text was banned. The
  // owner-only editor receives the original through a different API endpoint.
  if (textStatus === 'banned') return wrapContent(<CensoredContent kind="text" />, 'before');
  // HTML supplies placement only. Playback identity and permissions come solely
  // from the media DTOs attached to this particular reaction by the server.
  const attachments = new Map();
  if (Array.isArray(media)) media.forEach(item => {
    if (item && typeof item.id === 'string' && item.id && ['audio', 'video'].includes(item.kind)
      && !attachments.has(mediaKey(item.id))) attachments.set(mediaKey(item.id), item);
  });
  const placed = new Set();
  let quotePlaced = false;
  const containsVideo = node => attachments.get(mediaKey(node.attribs?.['data-recorded-media-id'] || ''))?.kind === 'video'
    || (node.children || []).some(containsVideo);
  const options = {
    replace: node => {
      // Player frames are created only by ExternalLink after server validation.
      if (unsafeContainers.has(node.name)) return <></>;
      if (node.name === 'div' && node.attribs?.[QUOTE_ATTRIBUTE] === 'true') {
        // Only the first top-level marker may place this reaction's authorized
        // quote. A copied marker cannot fetch or impersonate another source.
        if (quotePlaced || node.parent || !quote) return <></>;
        quotePlaced = true;
        return <QuoteSlot>{quote}</QuoteSlot>;
      }
      if (node.name === 'span' && Object.prototype.hasOwnProperty.call(node.attribs || {}, 'data-recorded-media-id')) {
        const id = node.attribs['data-recorded-media-id'];
        if (!uuid.test(id)) return <></>;
        const key = mediaKey(id);
        const attachment = attachments.get(key);
        if (placed.has(key)) return <></>;
        let parent = node.parent;
        while (parent) {
          if (unsafeContainers.has(parent.name) || ['a', 'code', 'pre'].includes(parent.name)) return <></>;
          parent = parent.parent;
        }
        placed.add(key);
        // A retained comment can still contain the placement of a recording
        // erased with its owner. Never fetch by this untrusted HTML identity.
        if (!attachment) return <UnavailableRecording key={key} />;
        return <RecordedMediaPlayer key={key} media={attachment} reactionId={reactionId} inline />;
      }
      if (node.name === 'img') {
        if (isCensoredImage(node.attribs?.src)) return <CensoredContent />;
        if (isUnavailableImage(node.attribs?.src)) return <CensoredContent unavailable />;
        const src = imageSource(node.attribs?.src);
        return src ? <ContentImage src={src} alt={node.attribs?.alt || ''} /> : <></>;
      }
      if (node.name === 'a') {
        const href = normalizeExternalUrl(node.attribs?.href);
        // Preserve image content without retaining an unchecked surrounding URL.
        if (hasImage(node)) return <>{domToReact(node.children || [], options)}</>;
        return href ? <ExternalLink href={href} label={textContent(node)} customLabel={node.attribs?.[LINK_LABEL_ATTRIBUTE]} /> : <>{textContent(node)}</>;
      }
      if (node.type === 'text' && !['code', 'pre'].includes(node.parent?.name)) {
        const segments = externalLinkSegments(node.data);
        if (segments.some(segment => segment.href)) return <>{segments.map((segment, index) => segment.href
          ? <ExternalLink key={index} href={segment.href} label={segment.text} customLabel={segment.customLabel} /> : segment.text)}</>;
      }
      // Treat stored/translated/legacy HTML as untrusted too. Never forward
      // event handlers, CSS, srcdoc, form actions or unknown DOM attributes.
      // A video contains a modal dialog; it cannot be nested in a paragraph.
      // Keep the paragraph's spacing and text order in a flow-content wrapper.
      if (node.name === 'p' && containsVideo(node)) {
        return <div className="reaction-media-paragraph">{domToReact(node.children || [], options)}</div>;
      }
      if (node.name) return textTags.has(node.name)
        ? React.createElement(node.name, {}, node.name === 'br' ? undefined : domToReact(node.children || [], options))
        : <>{domToReact(node.children || [], options)}</>;
      return undefined;
    },
  };
  const content = parse(html || '', options);
  const remaining = Array.from(attachments).filter(([key]) => !placed.has(key)).map(([, item]) => item);
  const nodes = Array.isArray(content) ? content : [content];
  const position = nodes.findIndex(node => node?.type === QuoteSlot);
  const unplaced = <RecordedMedia media={remaining} reactionId={reactionId} />;
  if (position < 0) return <>{wrapContent(<>{content}{unplaced}</>, 'before')}{quote}</>;
  const after = nodes.slice(position + 1);
  return <>{wrapContent(<>{nodes.slice(0, position)}{unplaced}</>, 'before')}{quote}
    {after.some(meaningful) && wrapContent(<>{after}</>, 'after')}</>;
}
